/** Pure geo helpers. Kept side-effect free so they're trivially testable. */

const EARTH_RADIUS_M = 6_371_000;
const DEG2RAD = Math.PI / 180;

/** Great-circle distance in metres between two WGS84 points (haversine). */
export function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dLat = (lat2 - lat1) * DEG2RAD;
  const dLng = (lng2 - lng1) * DEG2RAD;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * DEG2RAD) * Math.cos(lat2 * DEG2RAD) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** Format a distance for HUD readouts. `null`/undefined (no fix yet) → "—".
 *  Under 1 km reads in metres ("420M"); at/over 1 km reads in km ("1.25KM"). */
export function formatDistance(meters: number | null | undefined): string {
  if (meters == null || !Number.isFinite(meters)) return '—';
  if (meters < 1000) return `${Math.round(meters)}M`;
  return `${(meters / 1000).toFixed(2)}KM`;
}

/** A closed ring of [lng, lat] points approximating a circle of `radiusM`
 *  around a centre, for a GeoJSON Polygon. Equirectangular approximation —
 *  accurate to well under a metre at the ~40 m engagement scale. */
export function circleRing(
  lat: number,
  lng: number,
  radiusM: number,
  points = 64,
): [number, number][] {
  const ring: [number, number][] = [];
  const latR = radiusM / EARTH_RADIUS_M / DEG2RAD;
  const lngR = latR / Math.cos(lat * DEG2RAD);
  for (let i = 0; i <= points; i++) {
    const theta = (i / points) * 2 * Math.PI;
    ring.push([lng + lngR * Math.cos(theta), lat + latR * Math.sin(theta)]);
  }
  return ring;
}
